import type { RootStore } from '~/stores/root.store';
import dayjs from '~/common/dayjs';
import { trackError } from '~/services/logger/error.utils';
import { sumBy } from 'lodash';
import { IPayment } from '~/components/HomePage/HomeCards/home-cards.utils';

export class PaymentsController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async toggleCardOnFile(isEnabled: boolean) {
        const {
            paymentsService,
            userStore: {
                currUser: { account }
            }
        } = this.rootStore;
        const response = await paymentsService.toggleCardOnFile(isEnabled);
        account.updateCardOnFileEnabled(response);
        return response;
    }

    async getTransactionFees() {
        const {
            paymentsService,
            userStore: {
                currUser: { account }
            }
        } = this.rootStore;
        const response = await paymentsService.getTransactionFees();
        account.updateTransactionFees(response);
    }

    async fetchNumberOfActiveRecurringFiles() {
        const { paymentsService, paymentsUiStore } = this.rootStore;
        const response =
            await paymentsService.fetchNumberOfActiveRecurringFiles();
        paymentsUiStore.updateNumberOfActiveRecurringFiles(response);
    }

    async fetchMonthlyPayments() {
        const date = new Date();
        const firstDay = dayjs(
            new Date(date.getFullYear(), date.getMonth(), 1)
        ).format();
        const lastDay = dayjs(
            new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59)
        ).format();
        const { paymentsService, paymentsUiStore } = this.rootStore;
        try {
            const response = await paymentsService.fetchPayments(
                firstDay,
                lastDay,
                null
            );

            const verifiedMonthlyPayments = {
                ...response,
                upcoming_payments: this.GetPaymentsWithinCurrentMonth(
                    response?.upcoming_payments
                ),
                imminent_payments: this.GetPaymentsWithinCurrentMonth(
                    response?.imminent_payments
                )
            };

            paymentsUiStore.updateMonthlyPayments(verifiedMonthlyPayments);
            return true;
        } catch (error) {
            trackError({
                error,
                message:
                    'An error occurred when trying to fetch monthly payments for home payments card'
            });
            return false;
        }
    }

    async fetchYearlyPaymentsSum() {
        const { paymentsService, paymentsUiStore } = this.rootStore;
        try {
            const response = await paymentsService.fetchYearlyPaymentsSum();

            const data = response[0]?.booked_value_sum;
            paymentsUiStore.updateYearlyPaymentsSum(data);
            return true;
        } catch (error) {
            trackError({
                error,
                message:
                    'An error occurred when trying to fetch yearly payments sum for one home highlight'
            });
            return false;
        }
    }

    async fetchOverduePayments() {
        const { paymentsService, paymentsUiStore } = this.rootStore;
        try {
            const response = await paymentsService.fetchPayments(
                null,
                null,
                'late_payments'
            );
            paymentsUiStore.updateOverduePayments(response);
            return true;
        } catch (error) {
            trackError({
                error,
                message:
                    'An error occurred when trying to fetch overdue payments for home payments card'
            });
            return false;
        }
    }

    /**
     * Gets the total payments for a member.
     * @param {boolean} includeManualPayments - Whether to include "Mark as paid" payments or not.
     * @param {string} fromDate - The start date from which to count the payments.
     * @param {string} toDate - The end date until which to count the payments.
     * @returns {Promise<number>} The total amount of payments.
     */
    async getTotalPaidPayments({
        includeManualPayments,
        fromDate,
        toDate
    }: {
        fromDate: string;
        toDate: string;
        includeManualPayments: boolean;
    }) {
        const { paymentsService } = this.rootStore;
        const startDate = dayjs(fromDate).format();
        const endDate = dayjs(toDate).format();

        const { paid_payments: resPaidPayments } =
            await paymentsService.fetchPayments(
                startDate,
                endDate,
                'paid_payments'
            );

        // TODO: Implement this filter on the backend; time range support is missing for now.
        const paidPayments =
            resPaidPayments?.filter(currPayment => {
                const paymentDate = dayjs(currPayment.sent_on);
                return (
                    paymentDate.isAfter(startDate) &&
                    paymentDate.isBefore(endDate)
                );
            }) ?? [];

        const paidPaymentsSum = sumBy(paidPayments, currPayment => {
            if (!includeManualPayments && currPayment.is_manual) {
                return 0;
            }
            return currPayment.payment_amount;
        });

        return paidPaymentsSum;
    }

    GetPaymentsWithinCurrentMonth(unpaidPayments: IPayment[]) {
        const currentMonth = dayjs().month();

        return unpaidPayments?.filter(
            payment => dayjs(payment.payment_due_date).month() === currentMonth
        );
    }
}
